/**
 * StripeQuoteGeneratorPage — raise a native Stripe quote for a US customer and download its PDF.
 *
 * Sits beside the PDF-only Quote Generator; reached from the Stripe Quote Generator card in the
 * Billing Console. The page sends product keys and quantities only — every price is re-read by
 * staff-api (apps/api/src/app/stripe-quote/), so what is previewed is what Stripe is asked for.
 *
 * Flow: type the `cus_…` (looked up as it is typed) → pick the subscription / add-ons / hardware
 * → Preview (no Stripe write) → Create quote (create + finalize in Stripe) → Download PDF.
 * Nothing is accepted here. The operator's own quotes are listed underneath, from
 * `billing_stripe_quotes`, each with Extend: another 30 days for an open quote, or a finalized
 * revision for an expired one.
 *
 * Discounts are chosen per subscription / add-on line from the coupon catalog, and are never
 * offered on hardware or shipping.
 */
import { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router';
import { useApolloClient, useQuery } from '@apollo/client/react';
import { ArrowLeft, CalendarPlus, ExternalLink, FileDown, Plus, RotateCcw, Trash2 } from 'lucide-react';
import { Button, Input, Label } from '@staff-app/ui';
import {
  STRIPE_QUOTE_CATALOG,
  STRIPE_QUOTE_CREATE,
  STRIPE_QUOTE_CUSTOMER,
  STRIPE_QUOTE_EXTEND,
  STRIPE_QUOTE_PREVIEW,
  STRIPE_QUOTES,
} from '@staff-app/graphql-client';
import { LabeledSelect } from '../../components/console-ui';
import { DEFAULT_TERM_TEXTS } from '../../quote/terms';
import { downloadStripeQuotePdf } from '../quote-pdf';
import {
  ENTERPRISE,
  NONE,
  usd,
  type SqCatalog,
  type SqCustomer,
  type SqInput,
  type SqPreview,
  type SqRecord,
  type SqResult,
} from '../types';

/** Stripe's limits for the quote description and footer. */
const TEXT_MAX = 500;
/** A full customer id — Stripe's are `cus_` plus 14 characters; anything shorter is still being typed. */
const CUSTOMER_ID_RE = /^cus_[A-Za-z0-9]{14,}$/;
/** Pause after the last keystroke before the customer is looked up. */
const LOOKUP_DEBOUNCE_MS = 400;
const DEFAULT_FOOTER = DEFAULT_TERM_TEXTS.map((t) => `${t}.`).join('\n');

let seq = 0;
const nextId = () => `row-${++seq}`;

interface AddOnRow {
  id: string;
  key: string;
  quantity: string;
  tier: string;
  couponKey: string;
}
interface HardwareRow {
  id: string;
  /** `sku|stripePriceId`. */
  option: string;
  quantity: string;
}

function errText(e: unknown): string {
  return (e as Error)?.message || String(e);
}

export function StripeQuoteGeneratorPage() {
  const client = useApolloClient();
  const catalogQ = useQuery<{ stripeQuoteCatalog: SqCatalog }>(STRIPE_QUOTE_CATALOG, {
    fetchPolicy: 'network-only',
  });
  const catalog = catalogQ.data?.stripeQuoteCatalog;
  const mineQ = useQuery<{ stripeQuotes: SqRecord[] }>(STRIPE_QUOTES, {
    variables: { mine: true },
    fetchPolicy: 'network-only',
  });

  const [cusId, setCusId] = useState('');
  const [customer, setCustomer] = useState<SqCustomer | null>(null);
  const [plan, setPlan] = useState(NONE);
  const [screens, setScreens] = useState('');
  const [planCoupon, setPlanCoupon] = useState(NONE);
  const [addOns, setAddOns] = useState<AddOnRow[]>([]);
  const [hardware, setHardware] = useState<HardwareRow[]>([]);
  const [description, setDescription] = useState('');
  const [footer, setFooter] = useState(DEFAULT_FOOTER);

  const [preview, setPreview] = useState<SqPreview | null>(null);
  const [previewedKey, setPreviewedKey] = useState('');
  const [result, setResult] = useState<SqResult | null>(null);
  const [busy, setBusy] = useState<'' | 'preview' | 'create' | 'pdf' | 'extend'>('');
  const [lookingUp, setLookingUp] = useState(false);
  const [notice, setNotice] = useState('');
  const [error, setError] = useState('');

  const planDef = catalog?.plans.find((p) => p.alias === plan);
  const couponOptions = [
    { label: 'No discount', value: NONE },
    ...(catalog?.coupons ?? []).map((c) => ({
      label: c.discountSummary ? `${c.label} (${c.discountSummary})` : c.label,
      value: c.couponKey,
    })),
  ];
  const addOnChoices = (catalog?.addOns ?? []).filter(
    (a) => !a.requiresPlan.length || plan === ENTERPRISE || a.requiresPlan.includes(plan),
  );

  const input: SqInput = useMemo(() => {
    const opt = (o: string) => {
      const [sku, stripePriceId] = o.split('|');
      return { sku, stripePriceId: stripePriceId || undefined };
    };
    return {
      stripeCustomerId: cusId.trim(),
      plan: plan === NONE ? undefined : plan,
      screens: plan === NONE ? undefined : Number(screens || 0),
      planCouponKey: plan === NONE || planCoupon === NONE ? undefined : planCoupon,
      addOns: addOns
        .filter((a) => a.key)
        .map((a) => ({
          key: a.key,
          quantity: a.quantity ? Number(a.quantity) : undefined,
          tier: a.tier || undefined,
          couponKey: a.couponKey === NONE ? undefined : a.couponKey,
        })),
      hardware: hardware
        .filter((h) => h.option)
        .map((h) => ({ ...opt(h.option), quantity: Number(h.quantity || 0) })),
      description: description.trim() || undefined,
      footer: footer.trim() || undefined,
    };
  }, [cusId, plan, screens, planCoupon, addOns, hardware, description, footer]);
  const inputKey = JSON.stringify(input);
  const previewCurrent = !!preview && previewedKey === inputKey;

  // Look the customer up as soon as a whole id has been typed or pasted — no button.
  useEffect(() => {
    const id = cusId.trim();
    if (!CUSTOMER_ID_RE.test(id)) return;
    let stale = false;
    const t = setTimeout(async () => {
      setLookingUp(true);
      setError('');
      try {
        const { data } = await client.query<{ stripeQuoteCustomer: SqCustomer }>({
          query: STRIPE_QUOTE_CUSTOMER,
          variables: { stripeCustomerId: id },
          fetchPolicy: 'network-only',
        });
        if (!stale) setCustomer(data?.stripeQuoteCustomer ?? null);
      } catch (e) {
        if (!stale) {
          setCustomer(null);
          setError(errText(e));
        }
      } finally {
        if (!stale) setLookingUp(false);
      }
    }, LOOKUP_DEBOUNCE_MS);
    return () => {
      stale = true;
      clearTimeout(t);
      setLookingUp(false);
    };
  }, [cusId, client]);

  const runPreview = async () => {
    setBusy('preview');
    setError('');
    setResult(null);
    try {
      const { data } = await client.query<{ stripeQuotePreview: SqPreview }>({
        query: STRIPE_QUOTE_PREVIEW,
        variables: { input },
        fetchPolicy: 'network-only',
      });
      const p = data?.stripeQuotePreview ?? null;
      setPreview(p);
      setPreviewedKey(inputKey);
      if (p) setCustomer(p.customer);
    } catch (e) {
      setPreview(null);
      setError(errText(e));
    } finally {
      setBusy('');
    }
  };

  const create = async () => {
    setBusy('create');
    setError('');
    try {
      const { data } = await client.mutate<{ stripeQuoteCreate: SqResult }>({
        mutation: STRIPE_QUOTE_CREATE,
        variables: { input },
      });
      setResult(data?.stripeQuoteCreate ?? null);
      void mineQ.refetch();
    } catch (e) {
      setError(errText(e));
    } finally {
      setBusy('');
    }
  };

  const pdf = async (quoteId: string) => {
    setBusy('pdf');
    setError('');
    try {
      await downloadStripeQuotePdf(quoteId);
    } catch (e) {
      setError(errText(e));
    } finally {
      setBusy('');
    }
  };

  const extend = async (q: SqRecord) => {
    setBusy('extend');
    setError('');
    setNotice('');
    try {
      const { data } = await client.mutate<{ stripeQuoteExtend: SqResult }>({
        mutation: STRIPE_QUOTE_EXTEND,
        variables: { quoteId: q.quoteId },
      });
      const r = data?.stripeQuoteExtend;
      if (r) {
        const until = r.expiresAt ? new Date(r.expiresAt * 1000).toLocaleDateString() : '';
        setNotice(
          r.quoteId === q.quoteId
            ? `${r.number ?? r.quoteId} now expires ${until}.`
            : `${q.number ?? q.quoteId} had expired, so it was revised as ${r.number ?? r.quoteId}, valid until ${until}.` +
                (r.finalizeError ? ` It is not finalized yet: ${r.finalizeError}` : ''),
        );
      }
      void mineQ.refetch();
    } catch (e) {
      setError(errText(e));
    } finally {
      setBusy('');
    }
  };

  const reset = () => {
    setCusId('');
    setCustomer(null);
    setPlan(NONE);
    setScreens('');
    setPlanCoupon(NONE);
    setAddOns([]);
    setHardware([]);
    setDescription('');
    setFooter(DEFAULT_FOOTER);
    setPreview(null);
    setResult(null);
    setError('');
  };

  const onPlan = (v: string) => {
    setPlan(v);
    const min = catalog?.plans.find((p) => p.alias === v)?.minScreens ?? 1;
    // Enterprise starts at its minimum (25) so the operator never has to know the rule.
    if (v !== NONE && Number(screens || 0) < min) setScreens(String(min));
    if (v === NONE) {
      setPlanCoupon(NONE);
      setAddOns([]);
    }
  };

  return (
    <div className="mx-auto flex max-w-5xl flex-col gap-4 p-4" data-testid="stripe-quote-page">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <div className="flex items-center gap-2">
          <Button asChild variant="ghost" size="sm" className="cursor-pointer">
            <Link to="/staff/optihub">
              <ArrowLeft className="mr-1 h-4 w-4" />
              {'Billing Console'}
            </Link>
          </Button>
          <h1 className="text-lg font-semibold text-foreground">{'Stripe Quote Generator'}</h1>
        </div>
        <Button variant="outline" size="sm" className="cursor-pointer" onClick={reset} data-testid="sq-reset">
          <RotateCcw className="mr-1 h-4 w-4" />
          {'Clear'}
        </Button>
      </div>
      <p className="text-xs text-muted-foreground">
        {
          'Creates a real quote in Stripe for a US customer, yearly terms, and downloads its PDF. The quote is not accepted here — no invoice or subscription is created. Prices come from the live catalog and cannot be changed.'
        }
      </p>

      {catalogQ.error ? <p className="text-xs text-destructive">{catalogQ.error.message}</p> : null}
      {catalog && !catalog.stripeReady ? (
        <p className="text-xs text-destructive">{'Stripe is not configured in this environment.'}</p>
      ) : null}

      {/* ── Customer ── */}
      <Section title="Customer" hint="The Stripe customer id (cus_…). Name, email and address come from Stripe; edit them there.">
        <div className="flex flex-wrap items-end gap-2">
          <div className="flex min-w-[260px] flex-col gap-1.5">
            <Label htmlFor="sq-cus" className="text-xs">{'Stripe customer'}</Label>
            <Input
              id="sq-cus"
              value={cusId}
              placeholder="cus_…"
              onChange={(e) => {
                setCusId(e.target.value);
                setCustomer(null);
              }}
              data-testid="sq-customer-id"
            />
          </div>
          {lookingUp ? <span className="pb-2 text-xs text-muted-foreground">{'Looking up…'}</span> : null}
        </div>
        {customer ? (
          <div className="flex flex-col gap-1 text-xs" data-testid="sq-customer">
            <p className="text-foreground">
              <span className="font-medium">{customer.name || '(no name)'}</span>
              {customer.email ? ` · ${customer.email}` : ''}
              {customer.currency ? ` · ${customer.currency.toUpperCase()}` : ''}
            </p>
            <p className="text-muted-foreground">{customer.addressSummary || 'No address in Stripe'}</p>
            {customer.warnings.map((w) => (
              <p key={w} className="text-warning">{w}</p>
            ))}
          </div>
        ) : null}
      </Section>

      {/* ── Subscription ── */}
      <Section title="Subscription" hint="Yearly. The annual 10% is already in the price. Enterprise starts at 25 screens.">
        <div className="grid grid-cols-1 gap-3 md:grid-cols-3">
          <LabeledSelect
            id="sq-plan"
            label="Plan"
            value={plan}
            onChange={onPlan}
            placeholder="No subscription"
            options={[
              { label: 'No subscription', value: NONE },
              ...(catalog?.plans ?? []).map((p) => ({
                label: `${p.name} — ${usd(p.unitAmountMinor)}/screen/yr`,
                value: p.alias,
              })),
            ]}
            testId="sq-plan"
          />
          {plan !== NONE ? (
            <>
              <div className="flex flex-col gap-1.5">
                <Label htmlFor="sq-screens" className="text-xs">{'Screens'}</Label>
                <Input
                  id="sq-screens"
                  inputMode="numeric"
                  value={screens}
                  onChange={(e) => setScreens(e.target.value.replace(/[^0-9]/g, ''))}
                  data-testid="sq-screens"
                />
                {planDef && planDef.minScreens > 1 ? (
                  <p className="text-[11px] text-muted-foreground">{`Minimum ${planDef.minScreens}.`}</p>
                ) : null}
              </div>
              <LabeledSelect
                id="sq-plan-coupon"
                label="Discount"
                value={planCoupon}
                onChange={setPlanCoupon}
                placeholder="No discount"
                options={couponOptions}
                testId="sq-plan-coupon"
              />
            </>
          ) : null}
        </div>
        {catalog?.couponsUnavailable ? (
          <p className="text-[11px] text-warning">{'The coupon catalog cannot be read in this environment.'}</p>
        ) : null}
      </Section>

      {/* ── Add-ons ── */}
      {plan !== NONE ? (
        <Section title="Add-ons" hint="Yearly, per screen unless flat. Each line can carry its own discount.">
          {addOns.map((row) => {
            const def = catalog?.addOns.find((a) => a.key === row.key);
            const set = (patch: Partial<AddOnRow>) =>
              setAddOns((s) => s.map((r) => (r.id === row.id ? { ...r, ...patch } : r)));
            return (
              <div key={row.id} className="grid grid-cols-1 items-end gap-3 md:grid-cols-[2fr_1fr_1fr_2fr_auto]">
                <LabeledSelect
                  id={`sq-addon-${row.id}`}
                  label="Add-on"
                  value={row.key}
                  onChange={(v) => {
                    const d = catalog?.addOns.find((a) => a.key === v);
                    set({ key: v, tier: d?.tiers[0] ?? '', quantity: d?.quantified ? row.quantity || screens : '' });
                  }}
                  placeholder="Choose"
                  options={addOnChoices.map((a) => ({
                    label: `${a.label}${a.unitAmountMinor != null ? ` — ${usd(a.unitAmountMinor)}/yr` : ''}`,
                    value: a.key,
                  }))}
                  testId="sq-addon-key"
                />
                <div className="flex flex-col gap-1.5">
                  <Label className="text-xs">{'Screens'}</Label>
                  <Input
                    value={def?.quantified === false ? '1' : row.quantity}
                    disabled={def?.quantified === false}
                    inputMode="numeric"
                    onChange={(e) => set({ quantity: e.target.value.replace(/[^0-9]/g, '') })}
                    data-testid="sq-addon-qty"
                  />
                </div>
                {def?.tiers.length ? (
                  <LabeledSelect
                    id={`sq-addon-tier-${row.id}`}
                    label="Tier"
                    value={row.tier}
                    onChange={(v) => set({ tier: v })}
                    placeholder="Tier"
                    options={def.tiers.map((t) => ({ label: t, value: t }))}
                    testId="sq-addon-tier"
                  />
                ) : (
                  <div />
                )}
                <LabeledSelect
                  id={`sq-addon-coupon-${row.id}`}
                  label="Discount"
                  value={row.couponKey}
                  onChange={(v) => set({ couponKey: v })}
                  placeholder="No discount"
                  options={couponOptions}
                  testId="sq-addon-coupon"
                />
                <Button
                  variant="ghost"
                  size="sm"
                  className="cursor-pointer"
                  onClick={() => setAddOns((s) => s.filter((r) => r.id !== row.id))}
                  aria-label="Remove add-on"
                >
                  <Trash2 className="h-4 w-4" />
                </Button>
              </div>
            );
          })}
          <Button
            variant="outline"
            size="sm"
            className="w-fit cursor-pointer"
            onClick={() => setAddOns((s) => [...s, { id: nextId(), key: '', quantity: '', tier: '', couponKey: NONE }])}
            data-testid="sq-addon-add"
          >
            <Plus className="mr-1 h-4 w-4" />
            {'Add add-on'}
          </Button>
        </Section>
      ) : null}

      {/* ── Hardware ── */}
      <Section
        title="Hardware"
        hint="US addresses only. Shipping is added automatically (Standard). Hardware and shipping are never discounted."
      >
        {catalog && !catalog.freightConfigured ? (
          <p className="text-[11px] text-destructive">{'No freight product is configured here, so hardware cannot be quoted.'}</p>
        ) : null}
        {hardware.map((row) => {
          const set = (patch: Partial<HardwareRow>) =>
            setHardware((s) => s.map((r) => (r.id === row.id ? { ...r, ...patch } : r)));
          return (
            <div key={row.id} className="grid grid-cols-1 items-end gap-3 md:grid-cols-[3fr_1fr_auto]">
              <LabeledSelect
                id={`sq-hw-${row.id}`}
                label="Product"
                value={row.option}
                onChange={(v) => set({ option: v })}
                placeholder="Choose"
                options={(catalog?.hardware ?? [])
                  .filter((h) => h.quotable)
                  .map((h) => ({
                    label: `${h.name} — ${usd(h.unitAmountMinor)}`,
                    value: `${h.sku}|${h.stripePriceId}`,
                  }))}
                testId="sq-hw-product"
              />
              <div className="flex flex-col gap-1.5">
                <Label className="text-xs">{'Quantity'}</Label>
                <Input
                  value={row.quantity}
                  inputMode="numeric"
                  onChange={(e) => set({ quantity: e.target.value.replace(/[^0-9]/g, '') })}
                  data-testid="sq-hw-qty"
                />
              </div>
              <Button
                variant="ghost"
                size="sm"
                className="cursor-pointer"
                onClick={() => setHardware((s) => s.filter((r) => r.id !== row.id))}
                aria-label="Remove hardware"
              >
                <Trash2 className="h-4 w-4" />
              </Button>
            </div>
          );
        })}
        <Button
          variant="outline"
          size="sm"
          className="w-fit cursor-pointer"
          onClick={() => setHardware((s) => [...s, { id: nextId(), option: '', quantity: '1' }])}
          data-testid="sq-hw-add"
        >
          <Plus className="mr-1 h-4 w-4" />
          {'Add hardware'}
        </Button>
      </Section>

      {/* ── Text on the quote ── */}
      <Section title="On the quote" hint="Description prints beside the customer's address. Terms & conditions: one per line — Stripe prints them as one paragraph at the bottom of each page, each led by •.">
        <TextBox label="Description (optional)" value={description} onChange={setDescription} rows={3} testId="sq-description" />
        <TextBox label="Terms & conditions" value={footer} onChange={setFooter} rows={6} testId="sq-footer" />
      </Section>

      {/* ── Actions ── */}
      <div className="flex flex-wrap items-center gap-2">
        <Button
          variant="outline"
          className="cursor-pointer"
          disabled={!input.stripeCustomerId || busy !== ''}
          onClick={runPreview}
          data-testid="sq-preview"
        >
          {busy === 'preview' ? 'Pricing…' : 'Preview'}
        </Button>
        <Button
          className="cursor-pointer"
          disabled={!previewCurrent || busy !== ''}
          onClick={create}
          data-testid="sq-create"
        >
          {busy === 'create' ? 'Creating…' : 'Create quote in Stripe'}
        </Button>
        {preview && !previewCurrent ? (
          <span className="text-[11px] text-muted-foreground">{'The form changed — preview again before creating.'}</span>
        ) : null}
      </div>
      {error ? (
        <p className="text-xs text-destructive" data-testid="sq-error">{error}</p>
      ) : null}

      {preview ? <PreviewTable preview={preview} /> : null}

      {result ? (
        <Section title={result.number ? `Quote ${result.number}` : 'Quote created (draft)'} hint="">
          <div className="flex flex-col gap-1 text-xs" data-testid="sq-result">
            <p>
              {`Total ${usd(result.amountTotalMinor)} including tax ${usd(result.amountTaxMinor)} · ${result.status}`}
              {result.expiresAt ? ` · expires ${new Date(result.expiresAt * 1000).toLocaleDateString()}` : ''}
            </p>
            {result.finalizeError ? (
              <p className="text-destructive">{`Created but not finalized, so there is no PDF yet: ${result.finalizeError}`}</p>
            ) : null}
            {!result.recorded ? (
              <p className="text-warning">{'The quote exists in Stripe but was not added to My recent quotes.'}</p>
            ) : null}
            <div className="flex gap-2 pt-1">
              {result.number ? (
                <Button size="sm" className="cursor-pointer" disabled={busy !== ''} onClick={() => pdf(result.quoteId)} data-testid="sq-result-pdf">
                  <FileDown className="mr-1 h-4 w-4" />
                  {'Download PDF'}
                </Button>
              ) : null}
              <Button asChild size="sm" variant="outline" className="cursor-pointer">
                <a href={result.dashboardUrl} target="_blank" rel="noreferrer">
                  <ExternalLink className="mr-1 h-4 w-4" />
                  {'Open in Stripe'}
                </a>
              </Button>
            </div>
          </div>
        </Section>
      ) : null}

      {/* ── The operator's own quotes ── */}
      <Section title="My recent quotes" hint="Quotes you raised here. Extend gives another 30 days from today; an expired quote is revised (same lines and prices, new number).">
        {notice ? <p className="text-xs text-foreground" data-testid="sq-notice">{notice}</p> : null}
        {mineQ.error ? <p className="text-xs text-destructive">{mineQ.error.message}</p> : null}
        {mineQ.data && !mineQ.data.stripeQuotes.length ? (
          <p className="text-xs text-muted-foreground">{'No quotes yet.'}</p>
        ) : null}
        {mineQ.data?.stripeQuotes.length ? (
          <MyQuotes rows={mineQ.data.stripeQuotes} busy={busy !== ''} onPdf={(id) => pdf(id)} onExtend={extend} />
        ) : null}
      </Section>
    </div>
  );
}

function MyQuotes(props: {
  rows: SqRecord[];
  busy: boolean;
  onPdf: (quoteId: string) => void;
  onExtend: (q: SqRecord) => void;
}) {
  return (
    <div className="overflow-x-auto">
      <table className="w-full text-xs" data-testid="sq-mine">
        <thead className="text-left text-muted-foreground">
          <tr>
            <th className="py-1 pr-3">{'Quote'}</th>
            <th className="py-1 pr-3">{'Customer'}</th>
            <th className="py-1 pr-3">{'Total'}</th>
            <th className="py-1 pr-3">{'Created'}</th>
            <th className="py-1 pr-3">{'Expires'}</th>
            <th className="py-1 pr-3">{'Status'}</th>
            <th className="py-1" />
          </tr>
        </thead>
        <tbody>
          {props.rows.map((q) => (
            <tr key={q.quoteId} className="border-t">
              <td className="py-1 pr-3">{q.number ?? q.quoteId}</td>
              <td className="py-1 pr-3">{q.customerName || q.stripeCustomerId}</td>
              <td className="py-1 pr-3">{usd(q.amountTotalMinor)}</td>
              <td className="py-1 pr-3">{new Date(q.createdAt).toLocaleDateString()}</td>
              <td className="py-1 pr-3">{q.expiresAt ? new Date(q.expiresAt).toLocaleDateString() : '—'}</td>
              <td className={q.status === 'expired' ? 'py-1 pr-3 text-warning' : 'py-1 pr-3'}>{q.status}</td>
              <td className="flex justify-end gap-1 py-1">
                {q.number ? (
                  <Button size="sm" variant="ghost" className="h-7 cursor-pointer px-2" disabled={props.busy} onClick={() => props.onPdf(q.quoteId)} title="Download PDF">
                    <FileDown className="h-4 w-4" />
                  </Button>
                ) : null}
                {q.status === 'open' || q.status === 'expired' ? (
                  <Button
                    size="sm"
                    variant="outline"
                    className="h-7 cursor-pointer px-2"
                    disabled={props.busy}
                    onClick={() => props.onExtend(q)}
                    data-testid={`sq-extend-${q.quoteId}`}
                  >
                    <CalendarPlus className="mr-1 h-4 w-4" />
                    {'Extend 30 days'}
                  </Button>
                ) : null}
                <Button asChild size="sm" variant="ghost" className="h-7 cursor-pointer px-2" title="Open in Stripe">
                  <a href={q.dashboardUrl} target="_blank" rel="noreferrer">
                    <ExternalLink className="h-4 w-4" />
                  </a>
                </Button>
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

function Section(props: { title: string; hint: string; children: React.ReactNode }) {
  return (
    <section className="flex flex-col gap-3 rounded-lg border bg-card p-4 shadow-sm">
      <div className="flex flex-col gap-1">
        <h2 className="text-sm font-semibold text-foreground">{props.title}</h2>
        {props.hint ? <p className="text-xs text-muted-foreground">{props.hint}</p> : null}
      </div>
      {props.children}
    </section>
  );
}

function TextBox(props: { label: string; value: string; onChange: (v: string) => void; rows: number; testId: string }) {
  const over = props.value.length > TEXT_MAX;
  return (
    <div className="flex flex-col gap-1.5">
      <Label className="text-xs">{props.label}</Label>
      <textarea
        className="w-full rounded border bg-background px-2 py-1.5 text-xs focus:outline-none focus:ring-1 focus:ring-ring"
        rows={props.rows}
        value={props.value}
        onChange={(e) => props.onChange(e.target.value)}
        data-testid={props.testId}
      />
      <p className={over ? 'text-[11px] text-destructive' : 'text-[11px] text-muted-foreground'}>
        {`${props.value.length} / ${TEXT_MAX}`}
      </p>
    </div>
  );
}

function PreviewTable({ preview }: { preview: SqPreview }) {
  return (
    <Section title="Preview" hint="Before tax. Stripe adds tax from the customer's address when the quote is created.">
      <div className="overflow-x-auto">
        <table className="w-full text-xs" data-testid="sq-preview-table">
          <thead className="text-left text-muted-foreground">
            <tr>
              <th className="py-1 pr-3">{'Item'}</th>
              <th className="py-1 pr-3">{'Qty'}</th>
              <th className="py-1 pr-3">{'Unit'}</th>
              <th className="py-1 pr-3">{'Amount'}</th>
              <th className="py-1">{'Discount'}</th>
            </tr>
          </thead>
          <tbody>
            {preview.lines.map((l, i) => (
              <tr key={i} className="border-t">
                <td className="py-1 pr-3">{`${l.label}${l.recurring ? ' (yearly)' : ''}`}</td>
                <td className="py-1 pr-3">{l.quantity}</td>
                <td className="py-1 pr-3">{usd(l.unitAmountMinor)}</td>
                <td className="py-1 pr-3">{usd(l.amountMinor)}</td>
                <td className="py-1">{l.couponLabel ? `${l.couponLabel}: −${usd(l.discountMinor)}` : ''}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      <div className="flex flex-col items-end gap-0.5 text-xs" data-testid="sq-preview-totals">
        <p>{`Yearly subtotal ${usd(preview.recurringSubtotalMinor)}`}</p>
        {preview.recurringDiscountMinor ? <p>{`Discount −${usd(preview.recurringDiscountMinor)}`}</p> : null}
        {preview.oneTimeSubtotalMinor ? <p>{`Hardware & shipping ${usd(preview.oneTimeSubtotalMinor)}`}</p> : null}
        <p className="font-semibold">{`Total before tax ${usd(preview.totalBeforeTaxMinor)}`}</p>
        {preview.incomplete ? (
          <p className="text-warning">{'Some amounts are held in Stripe and not shown; Stripe will price them.'}</p>
        ) : null}
      </div>
    </Section>
  );
}
